'use client';

import { useEffect, useRef, useState, type FC, type KeyboardEvent } from 'react';
import { styled } from '@mui/material/styles';
import { withSe3AssetPrefix } from 'components/HomepageV3/assetPrefix';
import { FONTS } from 'components/HomepageV3/theme/tokens';
import {
  buildSe3ExploreRoute,
  useSe3QaSearch,
  withSe3QaOverride,
} from 'components/HomepageV3/utils/routes';
import { userMenu } from 'utils/links';

export type EditorialNavVariant = 'loggedOut' | 'loggedIn' | 'authPending';

export interface EditorialNavLink {
  label: string;
  href: string;
  primary?: boolean;
}

export interface EditorialNavSearchSuggestion {
  label: string;
  /** Canonical offering detail route opened when the suggestion is selected. */
  href: string;
  /** Additional terms that find the offering while preserving its display label. */
  searchTerms?: string[];
}

export interface EditorialNavProps {
  variant?: EditorialNavVariant;
  links?: EditorialNavLink[];
  dropdownLabel?: string;
  dropdownItems?: EditorialNavLink[];
  investDropdownItems?: EditorialNavLink[];
  searchPlaceholder?: string;
  showSearch?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onSearchSubmit?: (value: string) => void;
  searchSuggestions?: EditorialNavSearchSuggestion[];
  accountName?: string;
  accountMenuItems?: EditorialNavLink[];
  logInLabel?: string;
  signUpLabel?: string;
  onSearchClick?: () => void;
  onLogInClick?: () => void;
  onSignUpClick?: () => void;
  onAccountClick?: () => void;
  className?: string;
  'data-cy'?: string;
}

const DEFAULT_LINKS: EditorialNavLink[] = [
  { label: 'Invest', href: buildSe3ExploreRoute(), primary: true },
  { label: 'Learn', href: '/blog' },
];

const DEFAULT_DROPDOWN: EditorialNavLink[] = [
  {
    label: 'Pre-IPO Education',
    href: 'https://www.startengine.com/insights/private-market-education',
  },
  { label: 'Blog', href: '/blog' },
];

const DEFAULT_INVEST_DROPDOWN: EditorialNavLink[] = [{ label: 'Pre-IPO Deals', href: '/private' }];

const DEFAULT_ACCOUNT_MENU: EditorialNavLink[] = [
  { label: 'Portfolio', href: `${userMenu.portfolio}?se3=1` },
  { label: 'Venture Club', href: userMenu.ventureClub || '/venture-club' },
  { label: 'Settings', href: userMenu.settings },
  { label: 'Help Center', href: 'https://help.startengine.com/' },
  { label: 'Log Out', href: userMenu.logout },
];

const WORDMARK_SRC = withSe3AssetPrefix('/homepage-v3/se-wordmark.png');
const INVEST_IN_STARTENGINE_URL = 'https://invest.startengine.com';

const Shell = styled('header')({
  position: 'sticky',
  top: 0,
  zIndex: 1200,
  background: 'var(--night)',
});

const Nav = styled('nav')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '20px var(--pad)',
  borderBottom: '1px solid rgba(233,215,182,0.12)',
  '@media (min-width: 641px) and (max-width: 900px)': {
    padding: '20px 24px',
  },
  '@media (max-width: 640px)': {
    padding: '14px var(--pad)',
  },
});

const Left = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 44,
  '@media (min-width: 641px) and (max-width: 900px)': { gap: 0 },
  '@media (max-width: 640px)': { gap: 0 },
});

const Brand = styled('a')({
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
  flexShrink: 0,
});

const BrandImg = styled('img')({
  height: 30,
  width: 'auto',
  display: 'block',
});

const Links = styled('div')({
  display: 'flex',
  gap: 30,
  alignItems: 'center',
  '@media (min-width: 641px) and (max-width: 900px)': { display: 'none' },
  '@media (max-width: 640px)': { display: 'none' },
});

const navLinkBase = {
  fontFamily: FONTS.sans,
  fontSize: '13.5px',
  fontWeight: 500,
  letterSpacing: '0.16em',
  textTransform: 'uppercase' as const,
  color: 'var(--c-text-2)',
  textDecoration: 'none',
  transition: 'color 0.15s',
  cursor: 'pointer',
  '&:hover': { color: 'var(--champagne)' },
};

const NavLink = styled('a', {
  shouldForwardProp: (prop) => prop !== 'primary',
})<{ primary?: boolean }>(({ primary }) => ({
  ...navLinkBase,
  ...(primary ? { color: 'var(--champagne)' } : null),
}));

const InvestInStartEngineLink = styled('a')({
  fontFamily: FONTS.sans,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'var(--night)',
  background: 'var(--champagne)',
  padding: '12px 20px',
  borderRadius: 14,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  transition: 'background 0.15s',
  '&:hover': { background: '#f3e6cb' },
});

const Dropdown = styled('div')({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    height: 22,
  },
  '&:hover .se-nav-dd-menu, &:focus-within .se-nav-dd-menu': {
    opacity: 1,
    visibility: 'visible',
    transform: 'translateY(0)',
  },
  '&:hover .se-nav-link-dd svg, &:focus-within .se-nav-link-dd svg': {
    transform: 'rotate(180deg)',
    opacity: 1,
  },
  // On mobile the hamburger owns the account menu; hide the standalone
  // "My Account" dropdown so it isn't a duplicative second menu (Paige QA 2026-07-10).
  '@media (max-width: 900px)': {
    '&.se-account-dd': { display: 'none' },
  },
});

const DropdownTrigger = styled('a')({
  ...navLinkBase,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  '& svg': {
    width: 9,
    height: 9,
    opacity: 0.7,
    transition: 'transform 0.18s',
  },
});

const PrimaryDropdownTrigger = styled(DropdownTrigger)({
  color: 'var(--champagne)',
});

const DropdownMenu = styled('div')({
  position: 'absolute',
  top: '100%',
  left: -16,
  marginTop: 14,
  minWidth: 200,
  background: 'var(--night-deep)',
  border: '1px solid rgba(233,215,182,0.18)',
  borderRadius: 14,
  padding: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  opacity: 0,
  visibility: 'hidden',
  transform: 'translateY(-6px)',
  transition: 'opacity 0.16s ease, transform 0.16s ease, visibility 0.16s',
  boxShadow: '0 22px 50px rgba(0,0,0,0.5)',
  zIndex: 200,
  '& a': {
    fontFamily: FONTS.sans,
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--c-text-2)',
    textDecoration: 'none',
    padding: '11px 14px',
    borderRadius: 9,
    transition: 'background 0.14s, color 0.14s',
    '&:hover': {
      background: 'rgba(233,215,182,0.08)',
      color: 'var(--champagne)',
    },
  },
});

const AccountMenu = styled(DropdownMenu)({
  left: 'auto',
  right: -16,
});

const Right = styled('div')({
  display: 'flex',
  gap: 12,
  alignItems: 'center',
  '@media (min-width: 641px) and (max-width: 900px)': { gap: 9 },
  '@media (max-width: 640px)': { gap: 9 },
});

const Search = styled('form')({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 14px',
  borderRadius: 999,
  border: '1px solid rgba(233,215,182,0.28)',
  background: 'rgba(0,0,0,0.2)',
  transition: 'border-color 0.15s, background 0.15s',
  '&:focus-within, &:hover': {
    borderColor: 'var(--champagne)',
    background: 'rgba(0,0,0,0.32)',
  },
  '& svg': {
    width: 15,
    height: 15,
    color: 'var(--c-text-3)',
    flex: 'none',
  },
  '@media (max-width: 640px)': { display: 'none' },
});

const SearchSuggestions = styled('div')({
  position: 'absolute',
  top: 'calc(100% + 8px)',
  left: 0,
  right: 0,
  zIndex: 2,
  overflow: 'hidden',
  border: '1px solid rgba(233,215,182,0.3)',
  borderRadius: 12,
  background: 'var(--night)',
  boxShadow: '0 14px 28px rgba(0,0,0,0.3)',
});

const SearchSuggestion = styled('a')({
  display: 'block',
  width: '100%',
  padding: '10px 13px',
  border: 0,
  borderBottom: '1px solid rgba(233,215,182,0.1)',
  background: 'transparent',
  color: 'var(--cream)',
  fontFamily: FONTS.sans,
  fontSize: 13,
  textAlign: 'left',
  textDecoration: 'none',
  cursor: 'pointer',
  '&:last-child': { borderBottom: 0 },
  '&:hover, &:focus-visible': { background: 'rgba(233,215,182,0.12)', outline: 'none' },
});

const SearchInput = styled('input')({
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: '#fff',
  fontFamily: FONTS.sans,
  fontSize: 13,
  width: 150,
  '@media (min-width: 641px) and (max-width: 900px)': { width: 118 },
  '&::placeholder': { color: 'var(--c-text-3)' },
});

const MobileSearch = styled('form')({
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  alignItems: 'center',
  gap: 8,
  padding: '10px 0 14px',
  borderBottom: '1px solid rgba(233,215,182,0.08)',
  '& svg': {
    width: 15,
    height: 15,
    color: 'var(--c-text-3)',
  },
});

const MobileSearchField = styled('label')({
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '11px 13px',
  borderRadius: 12,
  border: '1px solid rgba(233,215,182,0.28)',
  background: 'rgba(0,0,0,0.24)',
  '&:focus-within': {
    borderColor: 'var(--champagne)',
    background: 'rgba(0,0,0,0.34)',
  },
});

const MobileSearchInput = styled('input')({
  minWidth: 0,
  width: '100%',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: '#fff',
  fontFamily: FONTS.sans,
  fontSize: 13,
  '&::placeholder': { color: 'var(--c-text-3)' },
});

const MobileSearchButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 42,
  padding: '0 14px',
  borderRadius: 12,
  border: '1px solid rgba(233,215,182,0.36)',
  background: 'var(--champagne)',
  color: 'var(--night)',
  fontFamily: FONTS.sans,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  cursor: 'pointer',
});

const GhostButton = styled('button')({
  fontFamily: FONTS.sans,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: '#fff',
  background: 'transparent',
  border: '1px solid rgba(233,215,182,0.3)',
  padding: '11px 18px',
  borderRadius: 14,
  cursor: 'pointer',
  transition: 'all 0.15s',
  whiteSpace: 'nowrap',
  '&:hover': { borderColor: 'var(--champagne)', color: 'var(--champagne)' },
  '@media (min-width: 641px) and (max-width: 900px)': {
    padding: '11px 14px',
    letterSpacing: '0.11em',
  },
  '@media (max-width: 640px)': { display: 'none' },
});

const SolidButton = styled('button')({
  fontFamily: FONTS.sans,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'var(--night)',
  background: 'var(--champagne)',
  border: 'none',
  padding: '12px 20px',
  borderRadius: 14,
  cursor: 'pointer',
  transition: 'background 0.15s',
  whiteSpace: 'nowrap',
  '&:hover': { background: '#f3e6cb' },
  '@media (min-width: 641px) and (max-width: 900px)': {
    padding: '12px 15px',
    letterSpacing: '0.11em',
  },
  '@media (max-width: 640px)': {
    boxSizing: 'border-box',
    minHeight: 44,
    padding: '9px 13px',
    fontSize: 10,
    letterSpacing: '0.1em',
  },
});

const MenuButton = styled('button')({
  display: 'none',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 9,
  background: 'transparent',
  border: '1px solid rgba(233,215,182,0.3)',
  borderRadius: 12,
  color: '#fff',
  cursor: 'pointer',
  '& svg': { width: 18, height: 18, display: 'block' },
  '@media (min-width: 641px) and (max-width: 900px)': { display: 'inline-flex' },
  '@media (max-width: 640px)': {
    display: 'inline-flex',
    boxSizing: 'border-box',
    width: 44,
    height: 44,
    padding: 0,
  },
});

const MobileMenu = styled('div', {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open: boolean }>(({ open }) => ({
  display: 'flex',
  flexDirection: 'column',
  maxHeight: open ? 'none' : 0,
  padding: open ? '4px var(--pad) 14px' : '0 var(--pad)',
  overflow: 'hidden',
  visibility: open ? 'visible' : 'hidden',
  pointerEvents: open ? 'auto' : 'none',
  background: 'var(--night-deep)',
  borderBottom: open ? '1px solid rgba(233,215,182,0.12)' : 'none',
  '@media (min-width: 901px)': { display: 'none' },
  '& > a, & > button': {
    padding: '13px 2px',
    fontFamily: FONTS.sans,
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: 'var(--c-text-2)',
    textDecoration: 'none',
    background: 'transparent',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    borderBottom: '1px solid rgba(233,215,182,0.08)',
    '&:last-of-type': { borderBottom: 'none' },
    '&:active': { color: 'var(--champagne)' },
  },
}));

const Portfolio = styled('a')({
  ...navLinkBase,
  display: 'inline-flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  marginRight: 4,
});

const Account = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  cursor: 'pointer',
  background: 'transparent',
  border: 'none',
  padding: 0,
  color: '#fff',
  '& .se-account-caret': {
    display: 'flex',
    alignItems: 'center',
    color: 'rgba(255,255,255,0.7)',
  },
  '& .se-account-caret svg': { width: 16, height: 16, display: 'block' },
});

const AccountName = styled('span')({
  fontFamily: FONTS.sans,
  fontSize: 14,
  fontWeight: 500,
  color: '#fff',
  letterSpacing: '0.01em',
});

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </svg>
  );
}

function CaretIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function HamburgerIcon() {
  return (
    <svg
      data-cy="editorial-nav-menu-icon-open"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      data-cy="editorial-nav-menu-icon-close"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

const EditorialNav: FC<EditorialNavProps> = ({
  variant = 'loggedOut',
  links = DEFAULT_LINKS,
  dropdownLabel = 'Learn',
  dropdownItems = DEFAULT_DROPDOWN,
  investDropdownItems = DEFAULT_INVEST_DROPDOWN,
  searchPlaceholder = 'Search offerings',
  showSearch = true,
  searchValue,
  onSearchChange,
  onSearchSubmit,
  searchSuggestions = [],
  accountName = 'My Account',
  accountMenuItems = DEFAULT_ACCOUNT_MENU,
  logInLabel = 'Log in',
  signUpLabel = 'Create account',
  onSearchClick,
  onLogInClick,
  onSignUpClick,
  onAccountClick,
  className,
  'data-cy': dataCy = 'editorial-nav',
}) => {
  const qaSearch = useSe3QaSearch();
  // During hydration useSe3QaSearch intentionally starts with the server's
  // empty snapshot. Fall back to the browser location for that first client
  // render so a forced tombstone assignment cannot be dropped by an immediate
  // nav click before the external-store subscription catches up.
  const qaHref = (href: string) => withSe3QaOverride(href, qaSearch || undefined);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [internalSearch, setInternalSearch] = useState('');
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLFormElement>(null);
  const [searchSuggestionsOpen, setSearchSuggestionsOpen] = useState(false);
  const isControlled = searchValue !== undefined;
  const value = isControlled ? searchValue : internalSearch;
  const normalizedSearch = value.trim().toLowerCase();
  const matchedSuggestions = normalizedSearch
    ? Array.from(
        new Map(
          searchSuggestions
            .filter((suggestion) =>
              [suggestion.label, ...(suggestion.searchTerms ?? [])].some((term) =>
                term.toLowerCase().includes(normalizedSearch),
              ),
            )
            .map((suggestion) => [suggestion.label.toLowerCase(), suggestion]),
        ).values(),
      ).slice(0, 5)
    : [];
  const isLoggedIn = variant === 'loggedIn';
  const showLoggedOutActions = variant === 'loggedOut';

  const handleSearchChange = (next: string) => {
    if (!isControlled) setInternalSearch(next);
    setSearchSuggestionsOpen(true);
    onSearchChange?.(next);
  };

  const submitSearch = (closeMenu = false) => {
    onSearchSubmit?.(value);
    if (closeMenu) setMobileOpen(false);
  };

  const showDropdown = Boolean(dropdownLabel && dropdownItems.length > 0);
  const dropdownTriggerHref = dropdownItems[0]?.href ?? '/blog';
  const renderedLinks = showDropdown ? links.filter((link) => link.label !== dropdownLabel) : links;
  const mobileLinks = [
    ...renderedLinks.flatMap((link) =>
      link.label === 'Invest' ? [link, ...investDropdownItems] : [link],
    ),
    ...(showDropdown ? dropdownItems : []),
  ];

  const handleShellKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape' || !mobileOpen) return;
    event.stopPropagation();
    setMobileOpen(false);
    menuButtonRef.current?.focus();
  };

  useEffect(() => {
    const closeSuggestionsOnOutsidePointerDown = (event: MouseEvent) => {
      if (searchRef.current?.contains(event.target as Node)) return;
      setSearchSuggestionsOpen(false);
    };

    document.addEventListener('mousedown', closeSuggestionsOnOutsidePointerDown);
    return () => document.removeEventListener('mousedown', closeSuggestionsOnOutsidePointerDown);
  }, []);

  return (
    <Shell className={className} data-cy={dataCy} onKeyDown={handleShellKeyDown}>
      <Nav aria-label="Primary" className="se-nav">
        <Left className="se-nav-left">
          <Brand
            href={qaHref('/')}
            aria-label="StartEngine"
            data-cy="editorial-nav-brand"
            className="se-logo"
          >
            <BrandImg className="se-logo-img" src={WORDMARK_SRC} alt="StartEngine" />
          </Brand>
          <Links className="se-nav-links" data-cy="editorial-nav-links">
            {renderedLinks.map((link) =>
              link.label === 'Invest' && investDropdownItems.length > 0 ? (
                <Dropdown
                  key={link.label}
                  className="se-nav-dd"
                  data-cy="editorial-nav-invest-dropdown"
                >
                  <PrimaryDropdownTrigger
                    href={qaHref(link.href)}
                    className="se-nav-link se-nav-link-dd primary"
                  >
                    {link.label}
                    <CaretIcon />
                  </PrimaryDropdownTrigger>
                  <DropdownMenu
                    className="se-nav-dd-menu"
                    data-cy="editorial-nav-invest-dropdown-menu"
                  >
                    {investDropdownItems.map((item) => (
                      <a
                        href={qaHref(item.href)}
                        key={item.label}
                        data-cy="editorial-nav-dropdown-item"
                      >
                        {item.label}
                      </a>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              ) : (
                <NavLink
                  key={link.label}
                  href={qaHref(link.href)}
                  primary={link.primary}
                  data-cy="editorial-nav-link"
                  className={link.primary ? 'se-nav-link primary' : 'se-nav-link'}
                >
                  {link.label}
                </NavLink>
              ),
            )}
            {showDropdown && (
              <Dropdown className="se-nav-dd" data-cy="editorial-nav-dropdown">
                <DropdownTrigger
                  href={qaHref(dropdownTriggerHref)}
                  className="se-nav-link se-nav-link-dd"
                >
                  {dropdownLabel}
                  <CaretIcon />
                </DropdownTrigger>
                <DropdownMenu className="se-nav-dd-menu" data-cy="editorial-nav-dropdown-menu">
                  {dropdownItems.map((item) => (
                    <a
                      href={qaHref(item.href)}
                      key={item.label}
                      data-cy="editorial-nav-dropdown-item"
                    >
                      {item.label}
                    </a>
                  ))}
                </DropdownMenu>
              </Dropdown>
            )}
            <InvestInStartEngineLink
              href={INVEST_IN_STARTENGINE_URL}
              data-cy="editorial-nav-invest-in-startengine"
            >
              Invest in StartEngine
            </InvestInStartEngineLink>
          </Links>
        </Left>

        <Right className="se-nav-right">
          {showSearch && (
            <Search
              ref={searchRef}
              role="search"
              data-cy="editorial-nav-search"
              className="se-search"
              onSubmit={(event) => {
                event.preventDefault();
                submitSearch();
              }}
            >
              <SearchIcon />
              <SearchInput
                className="se-search-input"
                type="text"
                value={value}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                onChange={(event) => handleSearchChange(event.target.value)}
                onClick={() => {
                  setSearchSuggestionsOpen(true);
                  onSearchClick?.();
                }}
                onFocus={() => {
                  setSearchSuggestionsOpen(true);
                  onSearchClick?.();
                }}
                data-cy="editorial-nav-search-input"
              />
              {searchSuggestionsOpen && matchedSuggestions.length > 0 && (
                <SearchSuggestions data-cy="editorial-nav-search-suggestions">
                  {matchedSuggestions.map((suggestion) => (
                    <SearchSuggestion
                      key={suggestion.label}
                      href={withSe3QaOverride(suggestion.href, qaSearch)}
                      data-cy="editorial-nav-search-suggestion"
                    >
                      {suggestion.label}
                    </SearchSuggestion>
                  ))}
                </SearchSuggestions>
              )}
            </Search>
          )}

          {isLoggedIn && (
            <>
              <Portfolio
                href={`${userMenu.portfolio}?se3=1`}
                data-cy="editorial-nav-portfolio"
                className="se-nav-link se-nav-portfolio"
              >
                My Portfolio
              </Portfolio>
              <Dropdown className="se-account-dd" data-cy="editorial-nav-account-dropdown">
                <Account
                  type="button"
                  onClick={onAccountClick}
                  data-cy="editorial-nav-account"
                  className="se-account se-nav-link-dd"
                >
                  <AccountName className="se-account-name">{accountName}</AccountName>
                  <span className="se-account-caret">
                    <CaretIcon />
                  </span>
                </Account>
                <AccountMenu className="se-nav-dd-menu" data-cy="editorial-nav-account-menu">
                  {accountMenuItems.map((item) => (
                    <a href={item.href} key={item.label} data-cy="editorial-nav-account-item">
                      {item.label}
                    </a>
                  ))}
                </AccountMenu>
              </Dropdown>
            </>
          )}

          {showLoggedOutActions && (
            <>
              <GhostButton
                type="button"
                onClick={onLogInClick}
                data-cy="editorial-nav-login"
                className="se-btn-ghost"
              >
                {logInLabel}
              </GhostButton>
              <SolidButton
                type="button"
                onClick={onSignUpClick}
                data-cy="editorial-nav-signup"
                className="se-btn-solid"
              >
                {signUpLabel}
              </SolidButton>
            </>
          )}

          <MenuButton
            ref={menuButtonRef}
            type="button"
            aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileOpen}
            aria-controls="editorial-nav-mobile-menu"
            onClick={() => setMobileOpen((open) => !open)}
            data-cy="editorial-nav-menu-toggle"
            className="se-nav-menu"
          >
            {mobileOpen ? <CloseIcon /> : <HamburgerIcon />}
          </MenuButton>
        </Right>
      </Nav>

      <MobileMenu
        id="editorial-nav-mobile-menu"
        open={mobileOpen}
        aria-hidden={!mobileOpen}
        inert={mobileOpen ? undefined : true}
        className={mobileOpen ? 'se-mobile-menu open' : 'se-mobile-menu'}
        data-cy="editorial-nav-mobile-menu"
      >
        {showSearch && (
          <MobileSearch
            role="search"
            data-cy="editorial-nav-mobile-search"
            onSubmit={(event) => {
              event.preventDefault();
              submitSearch(true);
            }}
          >
            <MobileSearchField>
              <SearchIcon />
              <MobileSearchInput
                type="text"
                value={value}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                onChange={(event) => handleSearchChange(event.target.value)}
                onClick={onSearchClick}
                onFocus={onSearchClick}
                data-cy="editorial-nav-mobile-search-input"
              />
            </MobileSearchField>
            <MobileSearchButton type="submit" data-cy="editorial-nav-mobile-search-submit">
              Search
            </MobileSearchButton>
          </MobileSearch>
        )}
        {mobileLinks.map((link) => (
          <a href={qaHref(link.href)} key={link.label} data-cy="editorial-nav-mobile-link">
            {link.label}
          </a>
        ))}
        <a href={INVEST_IN_STARTENGINE_URL} data-cy="editorial-nav-mobile-invest-in-startengine">
          Invest in StartEngine
        </a>
        {isLoggedIn && (
          <a href={`${userMenu.portfolio}?se3=1`} data-cy="editorial-nav-mobile-link">
            My Portfolio
          </a>
        )}
        {isLoggedIn &&
          accountMenuItems.map((item) => (
            <a href={item.href} key={item.label} data-cy="editorial-nav-mobile-link">
              {item.label}
            </a>
          ))}

        {showLoggedOutActions && (
          <>
            <button type="button" onClick={onLogInClick} data-cy="editorial-nav-mobile-link">
              {logInLabel}
            </button>
            <button type="button" onClick={onSignUpClick} data-cy="editorial-nav-mobile-link">
              {signUpLabel}
            </button>
          </>
        )}
      </MobileMenu>
    </Shell>
  );
};

export default EditorialNav;
